import React, { useCallback } from 'react';
import cn from 'classnames';

import IMG_BILL from 'assets/images/icons/pencil-purple.svg';
import IMG_REFUND from 'assets/images/icons/receipt-refund-purple.svg';
import IMG_USER from 'assets/images/icons/user-purple.svg';
import IMG_CIRCLE from 'assets/images/icons/x-circle-gray.svg';
import WizardModal from 'components/dialog/WizardModal.component';
import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import { useTranslation } from 'providers/GILT.provider';
import { fromEventToValue } from 'utils/converters/event.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import constant from 'utils/values/constant.util';

import CN from './CashupItemRefundWizard.module.scss';

const CTAG = 'CashupItemRefundWizard';
const L = logger(CTAG);

const STATE = Object.freeze({
  selectAction: 'select action page',
  clientAvailable: 'client available',
  denied: 'sorry you can not proceed page',
  closed: 'closed wizard',
});

const CLIENT_ACTION = constant({
  initial: 'initial',
  refund: 'refund',
  edit: 'edit',
});

const CLIENT_PRESENCE = constant({
  initial: 'initial',
  present: 'present',
  notPresent: 'not present',
});

const HELP_LINK =
  'http://help.getslick.com/en/articles/6884115-edit-a-bill-paid-via-slickpay-card-reader-if-client-is-not-present';

// NOTE: serves only to remove two props not found in <button> so that React doesn't write errors in console
const WizardButton = ({ wizardModalState, onNext, ...extra }) => <Button {...extra} />;

const CashupItemRefundWizard = ({ client, total, onChange }) => {
  const [state, setState] = useStateWhileMounted(STATE.selectAction);
  const [action, setAction] = useStateWhileMounted(CLIENT_ACTION.initial);
  const [presence, setPresence] = useStateWhileMounted(CLIENT_PRESENCE.initial);

  const { t } = useTranslation();

  const isConfirmDisabled = action === CLIENT_ACTION.initial;
  const isSelectedEdit = state === STATE.selectAction && action === CLIENT_ACTION.edit;
  const isSelectedRefund = state === STATE.selectAction && action === CLIENT_ACTION.refund;
  const isSelectedPresent = state === STATE.clientAvailable && presence === CLIENT_PRESENCE.present;
  const isSelectedNotPresent = state === STATE.clientAvailable && presence === CLIENT_PRESENCE.notPresent;

  const modalBanner = state === STATE.denied ? '' : 'backgroundImage';

  const handleAction = useCallback((ev) => void setAction(fromEventToValue(ev)), [setAction]);

  const handlePresence = useCallback((ev) => void setPresence(fromEventToValue(ev)), [setPresence]);
  const handleState = useCallback(({ value }) => void setState(value), [setState]);

  const close = useCallback(() => {
    setState(STATE.selectAction);
    setAction(CLIENT_ACTION.initial);
    setPresence(CLIENT_PRESENCE.initial);
  }, [setAction, setPresence, setState]);

  const handleCancel = useCallback(() => {
    call(`${CTAG}.onChange`, onChange, { refund: false, close: true });
    close();
  }, [close, onChange]);

  const handleClick = useCallback(() => {
    if (isSelectedRefund) {
      call(`${CTAG}.onChange`, onChange, { refund: true, close: true });

      return void close();
    }

    if (isSelectedEdit) {
      return void setState(STATE.clientAvailable);
    }

    if (isSelectedPresent) {
      call(`${CTAG}.onChange`, onChange, { refund: true, close: true });
      return void close();
    }

    if (isSelectedNotPresent) {
      call(`${CTAG}.onChange`, onChange, { refund: false, close: false });
      return void setState(STATE.denied);
    }

    if (state === STATE.denied) {
      call(`${CTAG}.onChange`, onChange, { refund: false, close: true });
      return void close();
    }

    return void L.warn('handleClick()', 'Unhandled case', { state, action, presence });
  }, [
    action,
    close,
    isSelectedEdit,
    isSelectedNotPresent,
    isSelectedPresent,
    isSelectedRefund,
    onChange,
    presence,
    setState,
    state,
  ]);

  return (
    <div data-bem="CashupItemRefundWizard">
      <WizardModal tag="CashupItemRefundWizard" state={state} onChange={handleState} isOpen banner={modalBanner}>
        <WizardModal.State value={STATE.selectAction}>
          <WizardModal.Header>
            <h2 className={CN.headerTitle}>{t('cashUp.cashupLogs.refundWizard.header')}</h2>
            <Button variant="empty" onClick={handleCancel}>
              <Icon variant="close" />
            </Button>
          </WizardModal.Header>
          <WizardModal.Body>
            <div className={CN.modalContent}>
              <div className={CN.title}>
                <Space>{client}</Space>
                {t('cashUp.cashupLogs.refundWizard.bill')}
                <Space>
                  <span className={CN.focus}>
                    <CurrencySign value={total} />
                  </span>
                </Space>
              </div>
              <div className={CN.subTitle}>{t('cashUp.cashupLogs.refundWizard.selectAction')}</div>
              <div className={CN.group}>
                <label
                  className={cn({
                    [CN.item]: true,
                    [CN.selected]: isSelectedRefund,
                  })}
                >
                  <div className={CN.inner}>
                    <input
                      className={CN.radio}
                      type="radio"
                      name="action"
                      value={CLIENT_ACTION.refund}
                      onChange={handleAction}
                    />
                    <div className={CN.iconContainer}>
                      <Icon variant={isSelectedRefund ? 'markCircle' : 'markCircleUnselected'} />
                    </div>

                    <div className={CN.imgContainer}>
                      <img src={IMG_REFUND} className={CN.image} alt="Refund payment icon" />
                    </div>
                  </div>
                  <div className={CN.itemTitle}>{t('cashUp.cashupLogs.refundWizard.refundPayment')}</div>
                </label>

                <label
                  className={cn({
                    [CN.item]: true,
                    [CN.selected]: isSelectedEdit,
                  })}
                >
                  <div className={CN.inner}>
                    <input
                      className={CN.radio}
                      type="radio"
                      name="action"
                      value={CLIENT_ACTION.edit}
                      onChange={handleAction}
                    />
                    <div className={CN.iconContainer}>
                      <Icon variant={isSelectedEdit ? 'markCircle' : 'markCircleUnselected'} />
                    </div>
                    <div className={CN.imgContainer}>
                      <img src={IMG_BILL} className={CN.image} alt="Edit bill icon" />
                    </div>
                  </div>

                  <div className={CN.itemTitle}>{t('cashUp.cashupLogs.refundWizard.editBill')}</div>
                </label>
              </div>
            </div>
          </WizardModal.Body>

          <WizardModal.Footer>
            <WizardButton variant="primary" casing="dialog" onClick={handleClick} disabled={isConfirmDisabled}>
              <span className={CN.buttonSpace}>{t('cashUp.cashupLogs.buttons.confirm')}</span>
            </WizardButton>
          </WizardModal.Footer>
        </WizardModal.State>

        <WizardModal.State value={STATE.clientAvailable}>
          <WizardModal.Header>
            <h2 className={CN.headerTitle}>{t('cashUp.cashupLogs.refundWizard.editHeader')}</h2>
            <Button variant="empty" onClick={handleCancel}>
              <Icon variant="close" />
            </Button>
          </WizardModal.Header>
          <WizardModal.Body>
            <div className={CN.modalContent}>
              <div className={CN.title}>{t('cashUp.cashupLogs.refundWizard.isClient', { client })}</div>
              <div className={CN.subTitle}>{t('cashUp.cashupLogs.refundWizard.editDescription')}</div>
              <div className={CN.group}>
                <label
                  className={cn({
                    [CN.item]: true,
                    [CN.selected]: isSelectedPresent,
                  })}
                >
                  <div className={CN.inner}>
                    <input
                      className={CN.radio}
                      type="radio"
                      name="presence"
                      value={CLIENT_PRESENCE.present}
                      onChange={handlePresence}
                    />
                    <div className={CN.iconContainer}>
                      <Icon variant={isSelectedPresent ? 'markCircle' : 'markCircleUnselected'} />
                    </div>

                    <div className={CN.imgContainer}>
                      <img src={IMG_USER} className={CN.image} alt="client is present icon" />
                    </div>
                  </div>
                  <div className={CN.itemTitle}>{t('cashUp.cashupLogs.refundWizard.clientPresent')}</div>
                </label>

                <label
                  className={cn({
                    [CN.item]: true,
                    [CN.selected]: isSelectedNotPresent,
                  })}
                >
                  <div className={CN.inner}>
                    <input
                      className={CN.radio}
                      type="radio"
                      name="presence"
                      value={CLIENT_PRESENCE.notPresent}
                      onChange={handlePresence}
                    />
                    <div className={CN.iconContainer}>
                      <Icon variant={isSelectedNotPresent ? 'markCircle' : 'markCircleUnselected'} />
                    </div>
                    <div className={CN.imgContainer}>
                      <img src={IMG_CIRCLE} className={CN.image} alt="client is not present icon" />
                    </div>
                  </div>
                  <div className={CN.itemTitle}>{t('cashUp.cashupLogs.refundWizard.notClientPresent')}</div>
                </label>
              </div>
            </div>
          </WizardModal.Body>
          <WizardModal.Footer>
            <WizardButton variant="primary" onClick={handleClick} disabled={presence === CLIENT_PRESENCE.initial}>
              <span className={CN.buttonSpace}>{t('cashUp.cashupLogs.buttons.confirm')}</span>
            </WizardButton>
          </WizardModal.Footer>
        </WizardModal.State>

        <WizardModal.State value={STATE.denied}>
          <WizardModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={handleCancel}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </WizardModal.Header>
          <WizardModal.Body>
            <div className={CN.modalImage}>
              <div className={CN.iconAlertContainer}>
                <Icon variant="alert" />
              </div>
            </div>
            <div className={CN.modalAlertContent}>
              <div className={CN.modalAlertTitle}>{t('cashUp.cashupLogs.refundWizard.cantProceed')}</div>
              <span className={CN.modalAlertText}>
                <p>{t('cashUp.cashupLogs.refundWizard.denyDescription1')}</p>
                <p>
                  {t('cashUp.cashupLogs.refundWizard.denyDescription2Part1')} {S.quoteOpen}
                  {t('cashUp.cashupLogs.refundWizard.helpGuide')}
                  {S.quoteClose} {t('cashUp.cashupLogs.refundWizard.denyDescription2Part2')}
                </p>
              </span>
            </div>
          </WizardModal.Body>
          <WizardModal.Footer>
            <ExternalLink link={HELP_LINK} variant="secondary">
              {t('cashUp.cashupLogs.refundWizard.helpGuide').toUpperCase()}
            </ExternalLink>
          </WizardModal.Footer>
        </WizardModal.State>
      </WizardModal>
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default CashupItemRefundWizard;
